import { Link } from "react-router-dom";
import { Rating, BuyButton } from "@/components/ProductCard";
import { autoLabels, parsePrice } from "@/lib/badges";
import { Trophy, CheckCircle } from "@phosphor-icons/react";
import { PriceAlertForm } from "@/components/PriceAlertForm";

const rowLabel = "sticky left-0 z-10 bg-[#F9F8F6] text-left text-[10px] sm:text-xs uppercase tracking-wider text-[#5C5A56] font-semibold px-3 sm:px-4 py-3 w-20 min-w-[5rem] sm:w-32 sm:min-w-[8rem] border-r border-[#E5E0D8]";
const cell = "px-3 sm:px-4 py-3 align-top text-sm text-[#2C2B29] min-w-[10.5rem] w-56";

export function ComparisonTable({ products, labels = {}, title = "Side-by-side comparison", testid = "comparison-table" }) {
  if (!products || products.length < 2) return null;
  const badges = autoLabels(products, labels);
  const prices = products.map((p) => parsePrice(p.price)).filter(Boolean);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const maxRating = Math.max(...products.map((p) => p.rating || 0));
  const rows = [
    ["Price", (p) => (
      <span className="font-semibold text-base">{p.price || "—"}
        {parsePrice(p.price) === minPrice && <span className="ml-2 text-[10px] font-bold uppercase text-[#5B7B53]">Lowest</span>}
      </span>
    )],
    ["Rating", (p) => (
      <div><Rating value={p.rating} /><span className="text-xs text-[#5C5A56]">{p.rating}★ {p.rating === maxRating && <b className="text-[#5B7B53]">· Top rated</b>}</span></div>
    )],
    ["Reviews", (p) => <span>{(p.review_count || 0).toLocaleString()}</span>],
    ["Why buy", (p) => (
      <ul className="space-y-1">
        {(p.benefits || []).slice(0, 3).map((b) => (
          <li key={b} className="flex gap-1.5 text-xs"><CheckCircle size={14} weight="fill" className="text-[#5B7B53] shrink-0 mt-0.5" />{b}</li>
        ))}
      </ul>
    )],
    ["Key specs", (p) => <span className="text-xs text-[#5C5A56]">{(p.features || []).slice(0, 4).join(" · ") || "—"}</span>],
    ["Watch out", (p) => <span className="text-xs text-[#5C5A56]">{(p.cons || [])[0] || "—"}</span>],
  ];

  return (
    <section data-testid={testid} className="not-prose my-10">
      {title && <h2 className="font-display text-2xl sm:text-3xl text-[#2C2B29] mb-4">{title}</h2>}
      <div className="overflow-x-auto rounded-2xl border border-[#E5E0D8] bg-white hh-no-scrollbar">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#E5E0D8]">
              <th className={rowLabel}>Product</th>
              {products.map((p, i) => (
                <th key={p.id} className={`${cell} text-left font-normal ${i === 0 ? "bg-[#5B7B53]/5" : ""}`}>
                  {badges[p.id] && (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-2 ${i === 0 ? "bg-[#D17A58] text-white" : "bg-[#EADDC8]/60 text-[#2C2B29]"}`}>
                      {i === 0 && <Trophy size={11} weight="fill" />}{badges[p.id]}
                    </span>
                  )}
                  <Link to={`/product/${p.id}`} className="block">
                    <img src={p.image_url} alt={p.title} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-xl mb-2" />
                    <span className="font-display text-base leading-snug text-[#2C2B29] hover:text-[#5B7B53] line-clamp-2">{p.title}</span>
                  </Link>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, render]) => (
              <tr key={label} className="border-b border-[#E5E0D8] last:border-0">
                <th scope="row" className={rowLabel}>{label}</th>
                {products.map((p, i) => <td key={p.id} className={`${cell} ${i === 0 ? "bg-[#5B7B53]/5" : ""}`}>{render(p)}</td>)}
              </tr>
            ))}
            <tr>
              <th scope="row" className={rowLabel}>Buy</th>
              {products.map((p, i) => (
                <td key={p.id} className={`${cell} ${i === 0 ? "bg-[#5B7B53]/5" : ""}`}>
                  <BuyButton product={p} testid={`${testid}-buy-${p.id}`} className="w-full !px-3 text-xs" />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {products.length > 2 && <p className={`${products.length > 4 ? "" : "sm:hidden"} text-xs text-[#5C5A56] mt-2`}>Scroll the table sideways to compare all {products.length} →</p>}
      <div className="mt-5 max-w-xl"><PriceAlertForm products={products} testid={`${testid}-alert`} compact /></div>
    </section>
  );
}
